import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface AdminWorkspace {
  id: string;
  name: string;
  slug: string | null;
  plan: string | null;
  status: string | null;
  created_at: string;
  memberCount: number;
  propertyCount: number;
}

export function useAdminWorkspaces() {
  return useQuery({
    queryKey: ["admin", "workspaces"],
    queryFn: async (): Promise<AdminWorkspace[]> => {
      const [wsRes, membersRes, propsRes] = await Promise.all([
        supabase
          .from("workspaces")
          .select("id, name, slug, plan, status, created_at")
          .order("created_at", { ascending: false }),
        supabase.from("workspace_members").select("workspace_id"),
        supabase.from("properties").select("workspace_id"),
      ]);

      if (wsRes.error) throw wsRes.error;

      const memberCountMap: Record<string, number> = {};
      for (const m of membersRes.data ?? []) {
        memberCountMap[m.workspace_id] =
          (memberCountMap[m.workspace_id] ?? 0) + 1;
      }

      const propCountMap: Record<string, number> = {};
      for (const p of propsRes.data ?? []) {
        propCountMap[p.workspace_id] = (propCountMap[p.workspace_id] ?? 0) + 1;
      }

      return (wsRes.data ?? []).map((ws) => ({
        ...ws,
        memberCount: memberCountMap[ws.id] ?? 0,
        propertyCount: propCountMap[ws.id] ?? 0,
      }));
    },
  });
}

export function useSuspendWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (workspaceId: string) => {
      const { error } = await supabase
        .from("workspaces")
        .update({ status: "suspended" })
        .eq("id", workspaceId);
      if (error) throw error;

      const { error: memberError } = await supabase
        .from("workspace_members")
        .update({ status: "inactive" })
        .eq("workspace_id", workspaceId);
      if (memberError) throw memberError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
      toast.success("Workspace suspended.");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useReactivateWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (workspaceId: string) => {
      const { error } = await supabase
        .from("workspaces")
        .update({ status: "active" })
        .eq("id", workspaceId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
      toast.success("Workspace reactivated.");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
